// Re-issuing keeps a scenario's alerts live: each alert ends `alert_minutes`
// after it is sent, and an alerts-only re-issue of the same issue updates it
// (POST /v1/issues/{id}/reissue-alerts). Auto re-issue fires a lead time
// before the alerts would end: half their life, and never less than five
// minutes, which is what ingest needs to see the new file (a 5-minute pull
// plus the viewer's 60 s watcher).

export const ALERT_MINUTES_DEFAULT = 15;
// A short life is how a demo lets a warning lapse quickly, so the issue
// accepts anything from a minute (the API agrees).
export const ALERT_MINUTES_MIN = 1;
export const ALERT_MINUTES_MAX = 1440;
// Auto re-issue is the exception: with the 5-minute lead, anything under 10
// would re-issue on every ingest pull — a new bucket key and a visible
// flicker each time — so it is only offered from ten minutes up.
export const AUTO_REISSUE_MIN_MINUTES = 10;
const MINUTE_MS = 60_000;
const MIN_LEAD_MINUTES = 5;

/** Whether auto re-issue can keep alerts of this life continuously live. */
export function autoReissueAllowed(alertMinutes) {
  return clampAlertMinutes(alertMinutes) >= AUTO_REISSUE_MIN_MINUTES;
}

/** Why auto re-issue is off for this life, or "" when it is allowed. */
export function autoReissueBlockedReason(alertMinutes) {
  if (autoReissueAllowed(alertMinutes)) return "";
  return `Auto re-issue needs alerts that live at least ${AUTO_REISSUE_MIN_MINUTES} minutes: a re-issue takes up to 6 minutes to reach consumers (a 5-minute pull plus the viewer's watcher), so shorter alerts would end before their successor shows. Re-issue by hand, or let them lapse.`;
}

export function clampAlertMinutes(value) {
  if (value === "" || value == null) return ALERT_MINUTES_DEFAULT;
  const v = Number(value);
  if (!Number.isFinite(v)) return ALERT_MINUTES_DEFAULT;
  return Math.min(ALERT_MINUTES_MAX, Math.max(ALERT_MINUTES_MIN, Math.round(v)));
}

/** How long before the alerts end a re-issue goes out. */
export function reissueLeadMs(alertMinutes) {
  return Math.max(clampAlertMinutes(alertMinutes) / 2, MIN_LEAD_MINUTES) * MINUTE_MS;
}

/** How often auto re-issue posts: alert life less the lead, at least a minute. */
export function reissueIntervalMs(alertMinutes) {
  return Math.max(MINUTE_MS, clampAlertMinutes(alertMinutes) * MINUTE_MS - reissueLeadMs(alertMinutes));
}

/** Milliseconds until the next auto re-issue, counted from the anchor (when
 * the last alerts were sent, on the browser's clock); 0 without one. */
export function nextReissueDelayMs(anchorMs, nowMs, alertMinutes) {
  if (!Number.isFinite(anchorMs)) return 0;
  return Math.max(0, anchorMs + reissueIntervalMs(alertMinutes) - nowMs);
}

/** When a response's alerts were sent, on the browser's clock: the receipt
 * time less how long before its reply the server sent them. Only the
 * server's own difference is used, so a skewed browser clock cannot shift
 * the schedule. Falls back to the receipt time. */
export function anchorFromResponse(response, receivedAtMs) {
  const sent = Date.parse(response?.sent);
  const at = Date.parse(response?.server_time);
  if (!Number.isFinite(sent) || !Number.isFinite(at)) return receivedAtMs;
  return receivedAtMs - Math.max(0, at - sent);
}

/** Scenario ids of a manifest whose onset (reference time + start hour) is
 * still ahead: a re-issue skips them until they start. */
export function notStartedYet(manifest, nowMs) {
  const ref = Date.parse(manifest?.reference_time);
  if (!Number.isFinite(ref)) return [];
  return (manifest.scenarios || [])
    .filter((s) => ref + (s.start_hour || 0) * 3_600_000 > nowMs)
    .map((s) => s.id);
}

const START_MARGIN_MS = 5000;

/** After a 422 "not started yet" ({ earliest_onset, server_time }): how long
 * to wait before trying again, from the server's own clock difference plus
 * a small margin; null when the detail does not say. */
export function retryWhenStartedMs(detail) {
  const onset = Date.parse(detail?.earliest_onset);
  const at = Date.parse(detail?.server_time);
  if (!Number.isFinite(onset) || !Number.isFinite(at)) return null;
  return Math.max(0, onset - at) + START_MARGIN_MS;
}
